/**
 * One-time localStorage migration for the iPaaS → Pipebase rebrand.
 *
 * Existing users have keys like `ipaas:workspace:name`, `ipaas:categories`,
 * etc. After the rebrand the SPA reads/writes `pipebase:*` instead.
 * This helper copies every `ipaas:*` key to `pipebase:*` if the new key
 * doesn't already exist, then deletes the old key.
 *
 * Idempotent: re-running does nothing because the `ipaas:*` keys are gone
 * after the first run, AND if for some reason both keys coexist, the
 * existing `pipebase:*` value wins (we never overwrite).
 *
 * Called exactly once from `main.tsx` before React mounts.
 */
export function migrateLocalStorage(): void {
  try {
    const oldPrefix = 'ipaas:';
    const newPrefix = 'pipebase:';
    const toMigrate: Array<{ oldKey: string; newKey: string }> = [];

    // Collect first, mutate after — Storage iteration order is stable
    // but `removeItem` during iteration is undefined behaviour.
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(oldPrefix)) continue;
      toMigrate.push({ oldKey: k, newKey: newPrefix + k.slice(oldPrefix.length) });
    }

    for (const { oldKey, newKey } of toMigrate) {
      const existingNew = localStorage.getItem(newKey);
      if (existingNew == null) {
        const oldVal = localStorage.getItem(oldKey);
        if (oldVal != null) localStorage.setItem(newKey, oldVal);
      }
      localStorage.removeItem(oldKey);
    }
  } catch {
    // localStorage unavailable / quota / privacy mode — silent no-op,
    // user just sees default values. Not worth surfacing.
  }
}
